import { parseDictValue } from '@/utils/dictValue.js'

/** 字典 code：在系统设置中配置各渠道参数 */
export const SHORT_LINK_DICT_CODE = 'short_link_config'

/** 淘宝短链名称最大字符数（含提交时追加的随机后缀） */
export const TAOBAO_NAME_MAX_LEN = 16

/**
 * 字典 value 格式（JSON 字符串存入 dict.value）：
 * {
 *   "taobao": { "sessionKey": "..." },
 *   "jd": { }
 * }
 */
export function parseShortLinkDict(raw) {
  const data = parseDictValue(raw) ?? raw
  if (!data || typeof data !== 'object') return { sessionKey: '' }
  const key =
    data.taobao?.sessionKey ||
    data.taobao?.session ||
    data.sessionKey ||
    data.session ||
    ''
  return { sessionKey: String(key).trim() }
}

/** 淘宝常见 sub_code 中文说明（前缀或包含匹配） */
export const TAOBAO_SUB_CODE_HINTS = [
  {
    key: 'isv.invalid-short-link-data',
    title: '输入生成短链数据错误！',
    hint: '请输入正确的创建短链数据。',
  },
  {
    key: 'isv.invalid-short-link-data-item',
    title: '商品ID输入错误！',
    hint: '请输入正确的商品ID。',
  },
  {
    key: 'isv.invalid-short-link-data-activity',
    title: '活动页地址必须为：taobao.com,tmall.com,jaeapp.com域名！',
    hint: '请输入正确的活动链接地址。',
  },
  {
    key: 'isv.invalid-short-link-name-exits',
    title: '短链名称已经存在请重新命名！',
    hint: '请重新输入短链名称。',
  },
  {
    key: 'isv.invalid-short-link-exists',
    title: '该短链已经存在，请勿重复生成！',
    hint: '请重新生成别的短链。',
  },
  {
    key: 'isv.invalid-short-link-name-exist',
    title: '短链名称已经存在请重新命名！',
    hint: '请重新输入短链名称。',
  },
  {
    key: 'isv.invalid-short-link-exist',
    title: '该短链已经存在，请勿重复生成！',
    hint: '请重新生成别的短链。',
  },
  {
    key: 'isv.permission-ip-whitelist-limit',
    title: '白名单限制。',
    hint: '请联系技术支持。',
  },
  {
    key: 'isv.invalid-short-link-name-limit',
    title: '短链名称超过限制',
    hint: '短链名称最多 16 个字符，请缩短名称。',
  },
]

export function matchTaobaoSubCodeHint(subCode) {
  if (subCode == null || subCode === '') return null
  const code = String(subCode).toLowerCase()
  for (const item of TAOBAO_SUB_CODE_HINTS) {
    const key = item.key.toLowerCase()
    if (code.includes(key) || key.includes(code)) return item
  }
  return null
}

export function isTaobaoSessionInvalidError(res) {
  const subCode = res?.data?.sub_code != null ? String(res.data.sub_code) : ''
  const subMsg = res?.data?.sub_msg != null ? String(res.data.sub_msg) : ''
  const innerMsg = res?.data?.msg != null ? String(res.data.msg) : ''
  return (
    subCode === 'invalid-sessionkey' ||
    /invalid.?session/i.test(subMsg) ||
    /invalid.?session/i.test(innerMsg) ||
    /sessionkey/i.test(subMsg)
  )
}

/** 外侧简要文案：msg + sub_msg（字段存在才拼接） */
export function formatTaobaoErrorSummary(res) {
  const outerMsg = res?.msg && res.msg !== 'Success' ? String(res.msg) : ''
  const data = res?.data && typeof res.data === 'object' ? res.data : {}
  const subMsg = data.sub_msg != null && data.sub_msg !== '' ? String(data.sub_msg) : ''
  const innerMsg = data.msg != null && data.msg !== '' ? String(data.msg) : ''

  if (isTaobaoSessionInvalidError(res)) {
    return '淘宝 SessionKey 非法或已失效，请联系管理员在系统设置中更新配置'
  }

  const parts = []
  if (outerMsg) parts.push(outerMsg)
  else if (innerMsg) parts.push(innerMsg)
  if (subMsg) parts.push(subMsg)

  const subCode = data.sub_code != null ? String(data.sub_code) : ''
  const hint = matchTaobaoSubCodeHint(subCode)
  if (hint && parts.length === 0) {
    return `${hint.title} ${hint.hint}`
  }
  if (hint) {
    return `${parts.join('：')}（${hint.title}）`
  }
  return parts.join('：') || '淘宝短链生成失败，请联系管理员处理'
}

/** 解析淘宝 API 错误，供消息与详情弹窗使用 */
export function parseTaobaoApiError(res) {
  const data =
    res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? { ...res.data } : {}
  const subCode = data.sub_code != null ? String(data.sub_code) : ''
  const subMsg = data.sub_msg != null ? String(data.sub_msg) : ''
  const innerMsg = data.msg != null ? String(data.msg) : ''
  const outerMsg = res?.msg && res.msg !== 'Success' ? String(res.msg) : ''
  const hint = matchTaobaoSubCodeHint(subCode)

  return {
    summary: formatTaobaoErrorSummary(res),
    outerMsg,
    subCode,
    subMsg,
    innerMsg,
    data,
    hint,
    isSessionInvalid: isTaobaoSessionInvalidError(res),
  }
}

/** @deprecated 使用 formatTaobaoErrorSummary */
export function formatTaobaoShortLinkError(res) {
  return formatTaobaoErrorSummary(res)
}

export function normalizeShortLinkEditData(raw) {
  const data = parseDictValue(raw) ?? (typeof raw === 'object' && raw ? raw : {})
  const { sessionKey } = parseShortLinkDict(data)
  const jd = data.jd && typeof data.jd === 'object' ? data.jd : {}
  return {
    taobao: { sessionKey },
    jd: { remark: jd.remark ?? '', ...jd },
  }
}

export const CHANNEL = {
  TAOBAO: 'taobao',
  JD: 'jd',
}

export const CHANNEL_LABEL = {
  [CHANNEL.TAOBAO]: '淘宝',
  [CHANNEL.JD]: '京东',
}

export const TAOBAO_SESSION_CHECK_URL =
  'https://m.duanqu.com/?_ariver_appid=3000000145404935&query=channel%3Dtm_FDY26_21_h&page=pages/home/home'

export function getNameCharCount(name) {
  return [...String(name || '')].length
}

export function randomAlphanumeric(len = 4) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let s = ''
  for (let i = 0; i < len; i++) {
    s += chars[Math.floor(Math.random() * chars.length)]
  }
  return s
}

/**
 * 淘宝请求用名称：截断原名称 + 随机字母后缀，总长度 ≤ 16
 */
export function buildTaobaoRequestName(baseName, seq = 0) {
  const base = String(baseName || '').trim()
  const suffix = seq > 0 ? `${randomAlphanumeric(3)}${seq}` : randomAlphanumeric(4)
  const sep = '-'
  const maxBaseLen = Math.max(1, TAOBAO_NAME_MAX_LEN - sep.length - suffix.length)
  const truncated = [...base].slice(0, maxBaseLen).join('')
  return `${truncated}${sep}${suffix}`
}

/** 预检查用极短随机名 */
export function buildRandomPrecheckName() {
  return `t${randomAlphanumeric(5)}`
}

/** 京东请求名：原名称 + 随机后缀（长度限制较宽） */
export function buildJdRequestName(baseName, seq = 0) {
  const base = String(baseName || '').trim()
  const suffix = seq > 0 ? `${randomAlphanumeric(4)}${seq}` : randomAlphanumeric(5)
  return `${base}-${suffix}`
}

/** 校验淘宝渠道下用户填写的展示名称（含后缀后不超过 16 字） */
export function validateTaobaoDisplayName(name) {
  const base = String(name || '').trim()
  if (!base) return '链接名称不能为空'
  const requestName = buildTaobaoRequestName(base)
  if (getNameCharCount(requestName) > TAOBAO_NAME_MAX_LEN) {
    return `链接名称过长，提交后总长度不能超过 ${TAOBAO_NAME_MAX_LEN} 个字符`
  }
  const minReserve = 5
  if (getNameCharCount(base) > TAOBAO_NAME_MAX_LEN - minReserve) {
    return `淘宝短链名称最多 ${TAOBAO_NAME_MAX_LEN - minReserve} 个字符（提交时会追加随机后缀）`
  }
  return null
}

const JD_URL_HINTS = ['mini-app-static.jd.com']
const TAOBAO_URL_HINTS = ['m.duanqu.com', 'duanqu.com']

export function detectChannelFromUrl(url) {
  if (!url || typeof url !== 'string') return null
  const lower = url.trim().toLowerCase()
  if (JD_URL_HINTS.some((h) => lower.includes(h))) return CHANNEL.JD
  if (TAOBAO_URL_HINTS.some((h) => lower.includes(h))) return CHANNEL.TAOBAO
  return null
}

const NAME_HEADER_KEYS = ['链接名称', '名称', 'name', '短链名称', 'shortlinkname']
const URL_HEADER_KEYS = ['长链接', '链接', 'url', 'longurl', 'long_url', 'taobaourl', '淘宝链接']

function normalizeHeader(key) {
  return String(key || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

export function matchHeaderKey(headers, candidates) {
  for (const key of headers) {
    const n = normalizeHeader(key)
    if (candidates.some((c) => normalizeHeader(c) === n)) return key
  }
  return null
}

function looksLikeUrl(val) {
  const s = String(val || '').trim().toLowerCase()
  return s.startsWith('http://') || s.startsWith('https://')
}

export function inferColumns(headers, sampleRows = []) {
  let nameKey = matchHeaderKey(headers, NAME_HEADER_KEYS)
  let urlKey = matchHeaderKey(headers, URL_HEADER_KEYS)

  if (!urlKey && sampleRows.length) {
    const scores = {}
    headers.forEach((h) => {
      scores[h] = sampleRows.filter((r) => looksLikeUrl(r[h])).length
    })
    const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0]
    if (best && best[1] > 0) urlKey = best[0]
  }

  if (!nameKey && headers.length) {
    nameKey = headers.find((h) => h !== urlKey) || headers[0]
  }
  if (!urlKey && headers.length) {
    urlKey = headers.find((h) => h !== nameKey) || headers[1] || headers[0]
  }

  return { nameKey, urlKey }
}

export function isValidUrl(url) {
  const s = String(url || '').trim().toLowerCase()
  return s.startsWith('http://') || s.startsWith('https://')
}

export function calcExpirationDate(fromDate = new Date()) {
  const d = new Date(fromDate)
  d.setDate(d.getDate() + 180)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export function parseSheetRows(json, nameKey, urlKey) {
  return json
    .map((row) => ({
      name: String(row[nameKey] ?? '').trim(),
      longUrl: String(row[urlKey] ?? '').trim(),
      shortUrl: '',
      status: '未生成',
      expirationDate: '',
    }))
    .filter((row) => row.name || row.longUrl)
}
